import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './ModelViewer.css';
import { getUrinaryInfo, resolveUrinaryName } from './urinaryData';
import { useFocusMeshOnLoad } from './useFocusMeshOnLoad';
import { useMeshSelection } from './useMeshSelection';
import ModelSearchBar from './ModelSearchBar';
import { extractInternals, FILTER_KEYWORDS, HIGHLIGHT_COLOR } from './modelViewerUtils';

function extractScene(mv) {
  const symbols = Object.getOwnPropertySymbols(mv);
  for (const sym of symbols) {
    const val = mv[sym];
    if (val && val.isScene) return val;
    if (val && val.scene && val.scene.isScene) return val.scene;
  }
  const keys = Object.getOwnPropertyNames(mv);
  for (const key of keys) {
    try {
      const val = mv[key];
      if (val && val.isScene) return val;
    } catch (e) {}
  }
  return null;
}

function buildMeshIndex(scene) {
  const index = new Map();
  scene.traverse((obj) => {
    if (!obj.isMesh) return;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const mat of mats) {
      if (mat && mat.name) {
        const key = mat.name.toLowerCase().trim();
        if (!index.has(key)) index.set(key, obj);
      }
    }
    if (obj.name) {
      const key = obj.name.toLowerCase().trim();
      if (!index.has(key)) index.set(key, obj);
    }
  });
  return index;
}

function UrinarySystemFull() {
  const navigate = useNavigate();
  const modelRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);

  const meshIndexRef = useRef(null);
  const highlightedMeshRef = useRef(null);
  const originalMaterialsRef = useRef(new Map());
  const originalRenderOrderRef = useRef(0);

  const forceRender = useCallback(() => {
    const mv = modelRef.current;
    if (!mv) return;
    if (mv.requestUpdate) { mv.requestUpdate(); return; }
    try {
      const expAttr = mv.getAttribute('exposure') || '1';
      const exp = parseFloat(expAttr);
      mv.setAttribute('exposure', (exp + 0.000001).toString());
      requestAnimationFrame(() => {
        if (modelRef.current) modelRef.current.setAttribute('exposure', expAttr);
      });
    } catch (e) {}
  }, []);

  const highlightMesh = useCallback((mesh) => {
    if (!mesh || !mesh.material) return;

    if (!originalMaterialsRef.current.has(mesh.uuid)) {
      const orig = mesh.material;
      let cloned;
      if (Array.isArray(orig)) {
        cloned = orig.map(m => (m && typeof m.clone === 'function') ? m.clone() : null).filter(Boolean);
      } else if (orig && typeof orig.clone === 'function') {
        cloned = orig.clone();
      }
      if (cloned) {
        mesh.material = cloned;
        const mats = Array.isArray(cloned) ? cloned : [cloned];
        const props = mats.map(m => ({
          color: (m.color && typeof m.color.clone === 'function') ? m.color.clone() : new THREE.Color(0xffffff),
          opacity: m.opacity ?? 1,
          transparent: !!m.transparent,
          depthWrite: m.depthWrite ?? true,
          emissive: (m.emissive && typeof m.emissive.clone === 'function') ? m.emissive.clone() : new THREE.Color(0x000000),
          emissiveIntensity: m.emissiveIntensity ?? 1,
        }));
        originalMaterialsRef.current.set(mesh.uuid, props);
      }
    }

    originalRenderOrderRef.current = mesh.renderOrder;

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach(m => {
      if (!m) return;
      if (m.color) m.color.set(HIGHLIGHT_COLOR);
      m.opacity = 0.75;
      m.transparent = true;
      m.depthWrite = false;
      if (m.emissive) {
        m.emissive.set(0x000000);
        m.emissiveIntensity = 0;
      }
    });
    mesh.renderOrder = 999;

    highlightedMeshRef.current = mesh;
    forceRender();
  }, [forceRender]);

  const resetHighlight = useCallback(() => {
    const mesh = highlightedMeshRef.current;
    if (!mesh || !mesh.isMesh) return;
    const props = originalMaterialsRef.current.get(mesh.uuid);
    if (props) {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((m, i) => {
        const p = props[i];
        if (m && p) {
          if (m.color && p.color) m.color.set(p.color);
          m.opacity = p.opacity;
          m.transparent = p.transparent;
          m.depthWrite = p.depthWrite;
          if (m.emissive && p.emissive) m.emissive.set(p.emissive);
          if (p.emissiveIntensity !== undefined) m.emissiveIntensity = p.emissiveIntensity;
        }
      });
    }
    mesh.renderOrder = originalRenderOrderRef.current;
    highlightedMeshRef.current = null;
    forceRender();
  }, [forceRender]);

  const { selectedPart, hotspotPosition, screenSide, hotspotKey, applySelection, closeLabel } =
    useMeshSelection({ infoLookup: getUrinaryInfo, highlightMesh, resetHighlight });

  const handleModelClick = useCallback((event) => {
    const mv = modelRef.current;
    if (!mv) return;

    const rect = mv.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const hit = mv.positionAndNormalFromPoint(x, y);
    if (!hit) {
      closeLabel();
      return;
    }

    let identifiedMesh = null;
    let identifiedName = null;

    const mvMat = mv.materialFromPoint(x, y);
    if (mvMat && mvMat.name) {
      const index = meshIndexRef.current;
      if (index) {
        identifiedMesh = index.get(mvMat.name.toLowerCase().trim()) || null;
      }
      if (identifiedMesh) {
        identifiedName = resolveUrinaryName(identifiedMesh);
      } else {
        identifiedName = mvMat.name;
      }
    }

    if (identifiedName && identifiedName !== 'Unknown') {
      applySelection(identifiedMesh, hit.position, hit.normal, identifiedName);
    } else {
      closeLabel();
    }
  }, [resetHighlight, highlightMesh, applySelection, closeLabel]);

  const onFound = useCallback((mesh, center, normal) => {
    const resolved = resolveUrinaryName(mesh);
    applySelection(mesh, center, normal, resolved);
  }, [applySelection]);

  const { focusMeshIfPending, focusMesh } = useFocusMeshOnLoad({ modelRef, onFound });

  useEffect(() => {
    const mv = modelRef.current;
    if (!mv) return;
    const handleLoad = () => {
      setIsLoading(false);
      const scene = extractScene(mv);
      if (scene) meshIndexRef.current = buildMeshIndex(scene);
      focusMeshIfPending();
    };
    mv.addEventListener('load', handleLoad);
    if (mv.loaded) handleLoad();
    return () => mv.removeEventListener('load', handleLoad);
  }, [focusMeshIfPending]);

  return (
    <div className="model-viewer-page skeleton-page skeleton-surface-click">
      <button onClick={() => navigate('/menu')} className="back-btn">← Back to Menu</button>

      <ModelSearchBar currentSystemId="urinarysystem" onFocusMesh={focusMesh} />

      {isLoading && (
        <div className="skeleton-mv-loading">
          <div className="skeleton-loading-spinner"></div>
          <p>Loading Urinary System...</p>
        </div>
      )}

      <model-viewer
        ref={modelRef}
        src="/models/UrinarySystem.glb"
        ios-src="/models/UrinarySystem.usdz"
        alt="3D Urinary System Model"
        ar
        ar-modes="webxr scene-viewer quick-look"
        camera-controls
        touch-action="none"
        shadow-intensity="1"
        loading="eager"
        reveal="auto"
        interaction-prompt="none"
        quick-look-browsers="safari chrome"
        camera-orbit="0deg 75deg 2m"
        field-of-view="45deg"
        min-camera-orbit="auto auto 0.05m"
        max-camera-orbit="auto auto 1m"
        onClick={handleModelClick}
        style={{ width: '100%', height: '100%', backgroundColor: '#ffffff', cursor: 'var(--cursor-pointer)', '--progress-bar-color': '#00E5FF', '--progress-bar-height': '4px' }}
      >
        {selectedPart && hotspotPosition && (
          <div key={hotspotKey} className={`skeleton-leader-hotspot ${screenSide}`} slot="hotspot-part" data-position={hotspotPosition.position} data-normal={hotspotPosition.normal}>
            <div className="skeleton-leader-dot"></div>
            <div className="skeleton-leader-line"></div>
            <div className="skeleton-leader-label">
              <div className="skeleton-leader-content">
                <span className="skeleton-leader-english">{selectedPart.english}</span>
                <span className="skeleton-leader-latin">{selectedPart.latin}</span>
              </div>
              <button className="skeleton-leader-close" onClick={closeLabel}>✕</button>
            </div>
          </div>
        )}
      </model-viewer>
    </div>
  );
}



function UrinarySystem() {

  return <UrinarySystemFull />;
}

export default UrinarySystem;
